// React & Hooks
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Tabulator } from 'react-tabulator/lib/types/TabulatorTypes';
import { useprogramManagement } from '@/domains/system-management/hooks/useProgramManagement';
// Types
import type { Params, Program } from '@/domains/system-management/types/program';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
// UI Components
import {
  type ColumnDefinition,
  ReactTabulator,
} from '@/shared/components/ui/LocalizedTabulator/LocalizedTabulator';
import LogList from '@/shared/components/ui/LogList/LogList';
import Select from '@/shared/components/ui/Select/Select';
// Custom Hooks
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { formatFullDateTimeDDMMYYYY } from '@/shared/utils/date';

const ProgramManagement = () => {
  const { t } = useTranslation();

  // tabulator top
  const commonColumnSettings = {
    hozAlign: 'center' as const,
    headerHozAlign: 'center' as const,
    resizable: false,
  };

  const columnsHistory = [
    {
      title: t('cmn > no'),
      formatter: (cell: Tabulator.CellComponent) => {
        const row = cell.getRow();
        const page = row.getTable().getPage();
        const pageSize = row.getTable().getPageSize();
        return (Number(page) - 1) * Number(pageSize) + row.getPosition(true);
      },
      width: 65,
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: 'ID',
      field: 'id',
      sorter: 'alphanum',
      headerSort: false,
      visible: false,
      ...commonColumnSettings,
    },
    {
      title: t('program > upper program'),
      field: 'upper_program',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: t('program > id'),
      field: 'lower_program',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: t('program > eng name'),
      field: 'eng',
      widthGrow: 2,
      headerSort: false,
      ...commonColumnSettings,
      formatter: (cell: Tabulator.CellComponent) => {
        const data = cell.getRow().getData();
        return data.lower_program ? `　└　${cell.getValue()}` : cell.getValue();
      },
    },
    {
      title: t('program > ind name'),
      field: 'ind',
      widthGrow: 2,
      headerSort: false,
      ...commonColumnSettings,
      formatter: (cell: Tabulator.CellComponent) => {
        const data = cell.getRow().getData();
        return data.lower_program ? `　└　${cell.getValue()}` : cell.getValue();
      },
    },
    {
      title: t('program > sort order'),
      field: 'sort_order',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnSettings,
    },
  ];

  const tbRef = useRef<Tabulator | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const programRef = useRef<HTMLInputElement | null>(null);
  const programGroupRef = useRef<HTMLInputElement | null>(null);

  //Disabled
  const [disabled, setDisabled] = useState<boolean>(true);
  const [disabledProgram, setDisabledProgram] = useState<boolean>(true);
  const [disabledProgramGroup, setDisabledProgramGroup] = useState<boolean>(true);

  //Change state
  const [hasChangesUpdate, setHasChangesUpdate] = useState<boolean>(false);
  const [hasChangesCreate, setHasChangesCreate] = useState<boolean>(false);
  const hasChangesUpdateRef = useRef(hasChangesUpdate);
  const hasChangesCreateRef = useRef(hasChangesCreate);
  const [newId, setNewId] = useState<string | null>(null);
  const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
  const [selectedIsProgramGroup, setSelectedIsProgramGroup] = useState('');
  const [selectedProgram, setSelectedProgram] = useState<{
    id?: string | null;
  }>({ id: null });

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
  }, [hasChangesUpdate, hasChangesCreate]);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  // Partial<Program> allows us to keep only some fields at a time
  // e.g. during create/update forms where not all fields are required yet
  const [formValues, setFormValues] = useState<Partial<Program>>({});

  const emptyDetail = () => {
    setFormValues({
      upper_program: '',
      lower_program: '',
      description: '',
      link_url: '',
      usage: '',
      eng: '',
      ind: '',
      sort_order: null,
      programType: '',
    });
  };

  // Button states
  const [buttonState, setButtonState] = useState({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  const disableAllButtons = () => {
    setButtonState({
      confirm: true,
      cancel: true,
      delete: true,
      create: true,
    });
  };

  const enableInitialButtons = () => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prevState) => ({
      ...prevState,
      create: false,
    }));
  };

  const enableUPDATEButtons = () => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
      delete: false,
      create: false,
    }));
  };

  const enableRegisterButtons = () => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
    }));
  };

  // CRUD Callbacks
  const reloadCallback = () => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    emptyDetail();
    setDisabled(true);
    setDisabledProgram(true);
    setDisabledProgramGroup(true);
    setHasChangesUpdate(false);
    setSelectedProgram({ id: null });
    setNewId(null);
    setIsNewClicked(false);
    setHasChangesCreate(false);
  };

  const updateCallback = () => {
    enableUPDATEButtons();
    setIsNewClicked(false);
    setHasChangesUpdate(false);
    setSelectedProgram({ id: null });
  };

  //params
  const [queryParams, setQueryParams] = useState<Params>({
    deletion: '001002',
    usage: '002001',
  });
  const [optionParams] = useState('upper-code=001&upper-code=002');

  const {
    usePrograms,
    useActivePrograms,
    useProgram,
    useCreateProgram,
    useCreateLowerProgram,
    useUpdateProgram,
    useUpdateLowerProgram,
    useDeleteProgram,
  } = useprogramManagement(queryParams);

  const { data: programsData } = usePrograms();
  const { data: activePrograms } = useActivePrograms();
  const { data: programData, error: programError } = useProgram(selectedProgram?.id ?? '');
  const createProgramMutation = useCreateProgram((responseData?: { id: string }) => {
    reloadCallback();
    const newUserId = responseData?.id ?? null;
    setNewId(newUserId);
  });
  const createLowerProgramMutation = useCreateLowerProgram((responseData?: { id: string }) => {
    reloadCallback();
    const newUserId = responseData?.id ?? null;
    setNewId(newUserId);
  });
  const updateProgramMutation = useUpdateProgram(
    (responseData?: { id: string; updated_by: string; updated_time: string }) => {
      updateCallback();
      const newUserId = responseData?.id ?? null;
      setNewId(newUserId);
    }
  );
  const updateLowerProgramMutation = useUpdateLowerProgram(
    (responseData?: { id: string; updated_by: string; updated_time: string }) => {
      const newUserId = responseData?.id ?? null;
      setNewId(newUserId);
      updateCallback();
    }
  );
  const deleteProgramMutation = useDeleteProgram(() => {
    reloadCallback();
  });

  const { commonListData } = useCommonCodes({ optionParams });

  const optionsRadioFilterDeleted = commonListData?.['001']
    ? [
        { value: 'All', label: t('cmn > all'), code: 'All' },
        ...(commonListData['001']?.code?.map((code, index) => ({
          value: code ?? '',
          label: commonListData['001']?.name[index] ?? '',
          code: code ?? '',
        })) || []),
      ]
    : [];

  const optionsRadioFilterUsage = commonListData?.['002']
    ? [
        { value: 'All', label: t('cmn > all'), code: 'All' },
        ...(commonListData['002']?.code?.map((code, index) => ({
          value: code ?? '',
          label: commonListData['002']?.name[index] ?? '',
          code: code ?? '',
        })) || []),
      ]
    : [];

  useEffect(() => {
    if (programError) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > load data fail'));
    }
  }, [programError, t]);

  const languageTabulator = () => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'), //text for the first page button
        first_title: t('cmn > first page'), //tooltip text for the first page button
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  };

  const optionsTabulator = {
    debugInvalidOptions: true,
    pagination: true,
    movableRows: false,
    resizableRows: false,
    locale: 'ko',
    langs: {
      ko: languageTabulator(),
    },
    index: 'id',
    paginationSize: 10,
    selectableRows: false,
    rowHeight: 41,
    footerElement: `<div id="footer-bottom" style="padding: 0 20px 0 0; text-align: right;">${t(
      'cmn > total'
    )} ${programsData?.length || 0} ${t('cmn > results')}</div>`,
    selectableRowsCheck: (row: Tabulator.RowComponent) => {
      return !row.getElement().classList.contains('tabulator-selected');
    },
    placeholder: `<div style='text-align:center;'>${t('msg > no data')}</div>`,
  };

  const handleRowSelected = (row: Tabulator.RowComponent) => {
    if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message
        .confirmClicked()
        .then(() => {
          tbRef.current?.deselectRow();
          row.select();
          row.getElement().classList;

          const rowData = row.getData();
          setSelectedProgram({
            id: rowData.id,
          });
          setDisabled(false);
          if (rowData.lower_program !== null) {
            setDisabledProgram(false);
            setDisabledProgramGroup(true);
            setFormValues((prevValues) => ({
              ...prevValues,
              programType: 'Program',
            }));
            requestAnimationFrame(() => {
              if (programRef.current) {
                programRef.current.focus();
              }
            });
          } else if (rowData.lower_program == null) {
            setDisabledProgramGroup(false);
            setDisabledProgram(true);
            setFormValues((prevValues) => ({
              ...prevValues,
              programType: 'Program Group',
            }));
            requestAnimationFrame(() => {
              if (programGroupRef.current) {
                programGroupRef.current.focus();
              }
            });
          }
          setHasChangesUpdate(false);
          setHasChangesCreate(false);
          setIsNewClicked(false);
        })
        .catch(() => {
          row.deselect();

          const prevId = selectedProgram.id;
          if (prevId) {
            const prevRow = tbRef.current?.getRow(prevId) || null;
            prevRow?.select();
            prevRow?.getElement().classList.add('custom-hover');
          }
        });
    } else {
      tbRef.current?.deselectRow();
      row.select();
      row.getElement().classList.add('custom-hover');

      const rowData = row.getData();
      setSelectedProgram((cur) => ({ ...cur, id: rowData.id }));
      setFormValues({
        id: rowData.id,
        eng: rowData.eng,
        ind: rowData.ind,
        upper_program: rowData.upper_program,
        lower_program: rowData.lower_program,
        programType: rowData.programType,
        usage: rowData.usage,
        description: rowData.description,
        sort_order: rowData.sort_order,
        link_url: rowData.link_url,
      });
      setDisabled(false);
      enableUPDATEButtons();
      setIsNewClicked(false);
      if (rowData.lower_program !== null) {
        setDisabledProgram(false);
        setDisabledProgramGroup(true);
        setFormValues((prevValues) => ({
          ...prevValues,
          programType: 'Program',
        }));
        requestAnimationFrame(() => {
          if (programRef.current) {
            programRef.current.focus();
          }
        });
      } else if (rowData.lower_program == null) {
        setDisabledProgramGroup(false);
        setDisabledProgram(true);
        setFormValues((prevValues) => ({
          ...prevValues,
          programType: 'Program Group',
        }));
        requestAnimationFrame(() => {
          if (programGroupRef.current) {
            programGroupRef.current.focus();
          }
        });
      }
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));

    if (isNewClicked) {
      setHasChangesCreate(true);
    } else {
      setHasChangesUpdate(true);
    }

    if (selectedProgram?.id) {
      if (!isNewClicked) {
        setHasChangesUpdate(true);
      }
    }
  };

  const handleOnChangeInputSelect = useCallback(({ target }: { target: { value: string } }) => {
    const { value } = target;
    setSelectedIsProgramGroup(value);
  }, []);

  const handleSearch = useCallback(
    (
      inputVal: string | null = null,
      radioVal: string | null = null,
      radioUsageVal: string | null = null
    ) => {
      const params: Params = {};
      if (inputVal) params.input = inputVal;
      if (radioVal && radioVal !== 'All') params.deletion = radioVal;
      if (radioUsageVal && radioUsageVal !== 'All') params.usage = radioUsageVal;
      if (selectedIsProgramGroup) params.upper_program = selectedIsProgramGroup;

      setQueryParams(params);
    },
    [selectedIsProgramGroup]
  );
  const handleCancelButtonClick = () => {
    if (hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        setHasChangesUpdate(false);
        emptyDetail();
        setIsNewClicked(false);
        setDisabled(true);
        setDisabledProgram(true);
        setDisabledProgramGroup(true);
        enableInitialButtons();
        setHasChangesUpdate(false);
      });
    } else if (isNewClicked) {
      if (hasChangesCreate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message.confirmClicked().then(() => {
          emptyDetail();
          setIsNewClicked(false);
          setDisabledProgram(true);
          setDisabledProgramGroup(true);
          setDisabled(true);
          enableInitialButtons();
          setHasChangesCreate(false);
        });
      } else {
        emptyDetail();
        setIsNewClicked(false);
        setDisabled(true);
        setDisabledProgram(true);
        setDisabledProgramGroup(true);
        enableInitialButtons();
        setHasChangesCreate(false);
        setSelectedProgram({ id: null });
      }
    } else {
      reloadCallback();
    }
  };
  const handleNewButtonClick = () => {
    if (selectedProgram?.id) {
      tbRef.current?.deselectRow();
      setDisabledProgram(false);
      setDisabledProgramGroup(true);
      emptyDetail();
      setFormValues((prevValues) => ({
        ...prevValues,
        programType: 'Program',
        upper_program: (programData as unknown as Program[])?.[0]?.upper_program ?? '',
      }));
      requestAnimationFrame(() => {
        if (programRef.current) {
          programRef.current.focus();
        }
      });
      setHasChangesUpdate(false);
      enableRegisterButtons();
      setIsNewClicked(true);
    } else {
      tbRef.current?.deselectRow();
      emptyDetail();
      setFormValues((prevValues) => ({
        ...prevValues,
        programType: 'Program Group',
      }));
      requestAnimationFrame(() => {
        if (programGroupRef.current) {
          programGroupRef.current.focus();
        }
      });

      setDisabled(false);
      setDisabledProgramGroup(false);
      setDisabledProgram(true);
      setIsNewClicked(true);
      enableRegisterButtons();
      setSelectedProgram({ id: null });
      setHasChangesUpdate(false);
    }
  };

  const handleRegistButtonClick = () => {
    if (selectedProgram?.id) {
      createLowerProgramMutation.mutateAsync(formValues);
    } else {
      createProgramMutation.mutateAsync(formValues);
    }
  };

  const handleConfirmButtonClick = () => {
    if (!formValues.lower_program) {
      updateProgramMutation.mutateAsync({
        id: String(selectedProgram?.id) ?? '',
        data: formValues,
      });
    } else {
      updateLowerProgramMutation.mutateAsync({
        id: String(selectedProgram?.id) ?? '',
        data: formValues,
      });
    }
  };

  const handleDeleteButtonClick = () => {
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('msg > delete confirm'), {
      mode: 'confirm',
    });
    message.confirmClicked().then(() => {
      deleteProgramMutation.mutateAsync(selectedProgram?.id ?? '');
    });
  };

  const handleReset = () => {
    setSelectedIsProgramGroup('');
  };

  useEffect(() => {
    if (programData && Array.isArray(programData)) {
      const firstDetail = programData[0];

      setFormValues((prevValues) => ({
        ...prevValues,
        eng: firstDetail?.eng,
        ind: firstDetail?.ind,
        upper_program: firstDetail?.upper_program,
        lower_program: firstDetail?.lower_program,
        usage: firstDetail?.usage,
        description: firstDetail?.description,
        sort_order: firstDetail?.sort_order,
        link_url: firstDetail?.link_url,
      }));
    }
  }, [programData]);

  const logs =
    programData && Array.isArray(programData)
      ? [
          {
            label: t('cmn > registered by'),
            value: programData[0]?.registered_by ?? '',
          },
          {
            label: t('cmn > registered time'),
            value: programData[0]?.registered_time
              ? formatFullDateTimeDDMMYYYY(new Date(programData[0].registered_time))
              : '',
          },
          {
            label: t('cmn > updated by'),
            value: programData[0]?.updated_by ?? '',
          },
          {
            label: t('cmn > updated time'),
            value: programData[0]?.updated_time
              ? formatFullDateTimeDDMMYYYY(new Date(programData[0].updated_time))
              : '',
          },
        ]
      : [];

  return (
    <>
      <section className="wrap">
        <div className="header-title">
          <h3>{t('SYSTEM')}</h3>
          <h3>&gt;</h3>
          <h3>{t('SYSTEM-PROGRAM')}</h3>
        </div>

        <ContainerCard>
          <Filtering
            placeholder={
              t('program > id') +
              ' / ' +
              t('program > program name') +
              ' / ' +
              t('program > url link') +
              ' / ' +
              t('program > description')
            }
            onSearch={handleSearch}
            onReset={handleReset}
            labelSelect={t('program > upper program')}
            optionsRadioFilter={optionsRadioFilterDeleted}
            optionsRadioFilterUsage={optionsRadioFilterUsage}
            isUsage={true}
            searchRef={searchRef}
          >
            <Select
              value={selectedIsProgramGroup}
              onChange={handleOnChangeInputSelect}
              className={'custom-select'}
              options={
                activePrograms
                  ? [
                      { value: '', label: t('cmn > all') },
                      ...activePrograms
                        .filter((item) => item.upper_program === item.id)
                        .map((item) => ({
                          value: item.upper_program,
                          label: item.eng,
                        })),
                    ]
                  : []
              }
            ></Select>
          </Filtering>
        </ContainerCard>
      </section>
      <ContainerCard>
        <ReactTabulator
          className="container-tabullator"
          id="program-management-table"
          data={programsData || []}
          columns={columnsHistory as unknown as ColumnDefinition[]}
          layout={'fitDataStretch'}
          options={optionsTabulator}
          onRef={(r) => {
            tbRef.current = r.current;
          }}
          events={{
            rowClick: (_e: UIEvent, row: Tabulator.RowComponent) => handleRowSelected(row),
            tableBuilt: () => {
              if (selectedProgram?.id) {
                const row = tbRef.current?.getRow(selectedProgram?.id) || null;
                row?.select();
                row?.getElement().classList.add('custom-hover');
              } else if (newId) {
                const row = tbRef.current?.getRow(newId) || null;

                if (row) {
                  tbRef.current?.setPageToRow(row);
                  tbRef.current?.scrollToRow(row, 'bottom', true);
                  row.select();
                  row.getElement().classList.add('custom-hover');
                  tbRef.current?.selectRow([newId]);
                  handleRowSelected(row);
                }
              }
            },
          }}
        />
      </ContainerCard>
      <ContainerCard>
        <div className="flex w-full flex-col gap-[10px]">
          <div className="grid grid-cols-3 gap-4">
            <div className="flex flex-col gap-4">
              <DetailForm
                label={t('program > program type')}
                value={formValues.programType}
                inputType={'select'}
                onChange={handleInputChange}
                name={'programType'}
                disabled={disabled}
                optionSelect={[
                  {
                    value: 'Program Group',
                    label: t('program > upper program'),
                    disabled: disabledProgramGroup,
                  },
                  {
                    value: 'Program',
                    label: t('program > id'),
                    disabled: disabledProgram,
                  },
                ]}
              />
              <DetailForm
                label={t('program > upper program')}
                value={formValues.upper_program || ''}
                inputType={'text'}
                onChange={handleInputChange}
                required={true}
                name={'upper_program'}
                formRef={programGroupRef}
                disabled={disabledProgramGroup}
              />
              <DetailForm
                label={t('program > id')}
                value={formValues.lower_program || ''}
                inputType={'text'}
                onChange={handleInputChange}
                name={'lower_program'}
                required={true}
                disabled={disabledProgram}
                formRef={programRef}
              />
            </div>
            <div className="flex flex-col gap-4">
              <DetailForm
                label={t('program > eng name')}
                value={formValues.eng || ''}
                inputType={'text'}
                onChange={handleInputChange}
                required={true}
                name="eng"
                disabled={disabled}
              />
              <DetailForm
                label={t('program > ind name')}
                value={formValues.ind || ''}
                inputType={'text'}
                onChange={handleInputChange}
                name={'ind'}
                required={true}
                disabled={disabled}
              />
              <DetailForm
                label={t('program > url link')}
                value={formValues.link_url || ''}
                inputType={'text'}
                onChange={handleInputChange}
                name={'link_url'}
                disabled={disabled}
              />
            </div>
            <div className="flex flex-col gap-4">
              <DetailForm
                label={t('cmn > usage')}
                name="usage"
                value={formValues.usage || ''}
                inputType={'select'}
                onChange={handleInputChange}
                required={true}
                disabled={disabled}
                optionSelect={
                  commonListData?.['002']
                    ? [
                        ...(commonListData['002']?.code?.map((code, index) => ({
                          value: code ?? '',
                          label: commonListData['002']?.name[index] ?? '',
                        })) || []),
                      ]
                    : []
                }
              />

              <DetailForm
                label={t('program > sort order')}
                value={formValues.sort_order || ''}
                inputType={'number'}
                onChange={handleInputChange}
                name={'sort_order'}
                disabled={disabled}
              />

              <DetailForm
                label={t('program > description')}
                value={formValues.description || ''}
                onChange={handleInputChange}
                name={'description'}
                inputType={'text'}
                disabled={disabled}
              />
            </div>
          </div>

          <hr className="border-t border-gray-300" />
          <div className="flex items-center justify-between gap-4 w-full">
            <div className="flex-1">{disabled ? null : <LogList logs={logs} />}</div>

            <div className="flex-none">
              <ButtonGroup
                onClickDelete={handleDeleteButtonClick}
                onClickNew={handleNewButtonClick}
                onClickCancel={handleCancelButtonClick}
                onClickRegist={handleRegistButtonClick}
                onClickConfirm={handleConfirmButtonClick}
                isNewClicked={isNewClicked}
                cancelButtonState={buttonState.cancel}
                confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
                deleteButtonState={buttonState.delete}
                newButtonState={hasChangesCreate ? false : buttonState.create}
                // Programs are seeded from postgres/initdb; this page only edits
                // and deletes them, so New/Register are hidden.
                showNew={false}
              />
            </div>
          </div>
        </div>
      </ContainerCard>
    </>
  );
};

export default ProgramManagement;
